import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import React, { useState, useRef, useEffect, useCallback } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PlaceholderOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    placeholder: {
      insertPlaceholder: (description: string) => ReturnType;
    };
  }
}

// ─── React NodeView ───────────────────────────────────────────────────────────

const PlaceholderNodeView: React.FC<NodeViewProps> = ({ node, getPos, editor }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const description = node.attrs.description || 'Enter value';

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleClick = useCallback(() => {
    if (!editor.isEditable) return;
    setIsEditing(true);
    setValue('');
  }, [editor.isEditable]);

  const handleConfirm = useCallback(() => {
    if (!value.trim()) {
      setIsEditing(false);
      return;
    }
    const pos = getPos();
    editor.chain()
      .focus()
      .command(({ tr }) => {
        tr.replaceWith(pos, pos + 1, editor.schema.text(value.trim()));
        return true;
      })
      .run();
    setIsEditing(false);
  }, [value, getPos, editor]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleConfirm();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setIsEditing(false);
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      if (value.trim()) {
        handleConfirm();
      } else {
        setIsEditing(false);
      }
      window.dispatchEvent(
        new CustomEvent('placeholder:navigate', { detail: { direction: e.shiftKey ? 'prev' : 'next' } })
      );
    }
  }, [value, handleConfirm]);

  if (isEditing) {
    return (
      <NodeViewWrapper as="span" className="placeholder-editing-wrapper">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            if (value.trim()) handleConfirm();
            else setIsEditing(false);
          }}
          placeholder={description}
          className="placeholder-input"
          style={{ width: `${Math.max(description.length, value.length) * 0.55 + 2.5}em` }}
        />
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper
      as="span"
      className="placeholder-pill"
      onClick={handleClick}
      contentEditable={false}
      title={`Click to fill: ${description}`}
    >
      <span className="placeholder-pill-icon">?</span>
      <span className="placeholder-pill-text">{description}</span>
    </NodeViewWrapper>
  );
};

// ─── TipTap Node Extension ───────────────────────────────────────────────────

export const Placeholder = Node.create<PlaceholderOptions>({
  name: 'placeholder',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      description: {
        default: 'Enter value',
        parseHTML: (element: HTMLElement) => element.getAttribute('data-description') || 'Enter value',
        renderHTML: (attributes: Record<string, string>) => ({ 'data-description': attributes.description }),
      },
      id: {
        default: '',
        parseHTML: (element: HTMLElement) => element.getAttribute('data-placeholder-id') || '',
        renderHTML: (attributes: Record<string, string>) => ({ 'data-placeholder-id': attributes.id }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-placeholder]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-placeholder': '',
        class: 'placeholder-pill',
      }),
      HTMLAttributes['data-description'] || 'Enter value',
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(PlaceholderNodeView);
  },

  addCommands() {
    return {
      insertPlaceholder: (description: string) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: {
            description,
            id: crypto.randomUUID(),
          },
        });
      },
    };
  },

  addKeyboardShortcuts() {
    return {
      Tab: ({ editor }) => {
        let hasPlaceholders = false;
        editor.state.doc.descendants((node) => {
          if (node.type.name === 'placeholder') hasPlaceholders = true;
        });
        if (!hasPlaceholders) return false;
        window.dispatchEvent(
          new CustomEvent('placeholder:navigate', { detail: { direction: 'next' } })
        );
        return true;
      },
      'Shift-Tab': ({ editor }) => {
        let hasPlaceholders = false;
        editor.state.doc.descendants((node) => {
          if (node.type.name === 'placeholder') hasPlaceholders = true;
        });
        if (!hasPlaceholders) return false;
        window.dispatchEvent(
          new CustomEvent('placeholder:navigate', { detail: { direction: 'prev' } })
        );
        return true;
      },
    };
  },
});
